import { ColumnDef } from '@tanstack/react-table'

import OrderStatusBadge from '@/components/misc/badges/OrderStatusBadge'
import { formatDateForTableSeparatedDateAndTime } from '@/utils/helpers'
import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import { Container } from '@/interfaces/container'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useLogisticContainer } from '../hooks/useLogisticContainer'
import { useState } from 'react'
import { useServerLogisticContainer } from '../hooks/useServerLogisticContainer'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { useTableContext } from './TableContext'
import { faTruck, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export const columns: ColumnDef<Container>[] = [
  {
    accessorKey: 'operationNumber',
    header: 'N° de operación',
    cell: ({ row }) => (
      <p className={'font-medium'}>{row.original.operationNumber || '-'}</p>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    cell: ({ row }) => (
      <span>{row.original.name || '-'}</span>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => <OrderStatusBadge status={row.original.status} />,
    enableHiding: false,
  },
  {
    accessorKey: 'categories',
    header: 'Categorías',
    cell: ({ row }) => (
      <span>{Array.isArray(row.original.categories) ? row.original.categories.map(cat => cat.name).join(', ') : '-'}</span>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'cost',
    header: 'Costo',
    cell: ({ row }) => (
      <span>{row.original.cost != null ? `$${row.original.cost}` : '-'}</span>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'percentProductsSales',
    header: '% Vendido',
    cell: ({ row }) => {
      const percent = row.original.percentProductsSales ?? 0;
      return (
        <div className="flex items-center gap-2 min-w-[100px]">
          <div className="w-full bg-gray-200 rounded h-3 overflow-hidden">
            <div
              className="bg-green-500 h-3 rounded"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <span className="ml-2 text-xs font-medium">{percent}%</span>
        </div>
      );
    },
    enableHiding: false,
  },
  {
    accessorKey: 'totalItems',
    header: 'Total de elementos',
    cell: ({ row }) => (
      <span>{row.original.totalItems || 0}</span>
    ),
    enableHiding: true,
  },
  {
    accessorKey: 'createdAt',
    header: 'Creado el',
    cell: ({ row }) => {
      const { date, time } = formatDateForTableSeparatedDateAndTime(row.original?.createdAt)
      return (
        <div className="flex flex-col items-start">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'ownerCompany',
    header: 'Proveedor',
    cell: ({ row }) => (
      <span>{row.original.ownerCompany?.props?.name || '-'}</span>
    ),
    enableHiding: true, // Ahora es ocultable
    // defaultHidden: true, // Oculta por defecto (manejada en DataTable)
  },
  {
    accessorKey: 'managerCompany',
    header: 'Manejador',
    cell: ({ row }) => (
      <span>{row.original.managerCompany?.name || '-'}</span>
    ),
    enableHiding: true, // Ahora es ocultable
    // defaultHidden: true, // Oculta por defecto (manejada en DataTable)
  },
  {
    accessorKey: 'importerCompany',
    header: 'Importadora',
    cell: ({ row }) => (
      <span>{row.original.importerCompany?.name || '-'}</span>
    ),
    enableHiding: true,
  },
  {
    accessorKey: 'transporterCompany',
    header: 'Transportista',
    cell: ({ row }) => (
      <span>{row.original.transporterCompany?.name || '-'}</span>
    ),
    enableHiding: true,
  },
  {
    accessorKey: 'storageCompany',
    header: 'Almacenaje',
    cell: ({ row }) => (
      <span>{row.original.storageCompany?.name || '-'}</span>
    ),
    enableHiding: true,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Última actualización',
    cell: ({ row }) => {
      const { date, time } = formatDateForTableSeparatedDateAndTime(row.original?.updatedAt)
      return (
        <div className="flex flex-col items-start">
          <span>{date}</span>
          <span>{time}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    id: 'identificativo',
    header: '', // Sin nombre de columna
    cell: ({ row }) => {
      const isGreen = row.original.isReturned || row.original.status === 'REFUNDED';
      return (
        <span className="relative flex items-center justify-center">
          <FontAwesomeIcon
            icon={faTruck}
            className="h-5 w-5 text-black"
          />
          <FontAwesomeIcon
            icon={faArrowLeft}
            className={`h-3 w-3 absolute right-0 bottom-0 ${isGreen ? 'text-green-600' : 'text-red-700'}`}
            style={{ transform: 'translate(30%, 30%)' }}
          />
        </span>
      )
    },
    enableHiding: false,
  },
  {
    id: 'actions',
    header: 'Acciones',
    cell: ({ row }) => {
      const { onRowClick } = useLogisticContainer();
      const { refetch } = useTableContext();
      const [showConfirm, setShowConfirm] = useState(false);
      const { deleteContainer } = useServerLogisticContainer();
      const deleteMutation = deleteContainer(row.original.id);
      return (
        <div className={'flex justify-start'}>
          <div role={'button'} className={`p-2`}>
            <PencilSquareIcon
              className={`h-5 w-5 text-blue-700`}
              onClick={e => {
                e.stopPropagation();
                onRowClick(row.original);
              }}
            />
          </div>
          <div role={'button'} className={`p-2`}>
            <TrashIcon
              className={`h-5 w-5 text-red-700`}
              onClick={e => {
                e.stopPropagation();
                setShowConfirm(true);
              }}
            />
            <AlertDialog open={showConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar contenedor?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción eliminará permanentemente el contenedor. ¿Desea continuar?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setShowConfirm(false)}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      deleteMutation.mutate(undefined, {
                        onSuccess: () => {
                          toast.success('Contenedor eliminado');
                          setShowConfirm(false);
                          refetch && refetch();
                        },
                        onError: () => {
                          toast.error('Error al eliminar contenedor');
                          setShowConfirm(false);
                        }
                      });
                    }}
                  >
                    Aceptar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )
    },
    enableHiding: false,
  },
]
